import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "./button";
import { Heading } from "./heading";
import { Alert } from "./alert";
import { showAlert } from "../alertService";
import {
  TrashIcon,
  PlusIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";

const CATEG_COLL = "proveedorCategorias";
const SUBCATEG_COLL = "proveedorSubcategorias";

const slugify = (str) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    // strip combining diacritical marks (U+0300–U+036F) so "ñ" → "n", "á" → "a", etc.
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const generateUniqueId = (baseSlug, existingIds) => {
  const slug = baseSlug || "item";
  if (!existingIds.has(slug)) return slug;
  let i = 2;
  while (existingIds.has(`${slug}-${i}`)) i++;
  return `${slug}-${i}`;
};

const isActivo = (item) => item?.activo !== false;

const ProveedoresCategoriasManager = () => {
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [selectedCategoriaId, setSelectedCategoriaId] = useState(null);
  const [newCategoriaName, setNewCategoriaName] = useState("");
  const [newSubcategoriaName, setNewSubcategoriaName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, "catalogos", CATEG_COLL, "items"),
      orderBy("orden")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setCategorias(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("Categorias snapshot error:", err);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, "catalogos", SUBCATEG_COLL, "items"),
      orderBy("orden")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setSubcategorias(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("Subcategorias snapshot error:", err);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    if (!selectedCategoriaId && categorias.length > 0) {
      setSelectedCategoriaId(categorias[0].id);
    }
  }, [categorias, selectedCategoriaId]);

  const visibleSubcategorias = useMemo(
    () => subcategorias.filter((s) => s.parentId === selectedCategoriaId),
    [subcategorias, selectedCategoriaId]
  );

  const handleAddCategoria = async () => {
    const nombre = newCategoriaName.trim();
    if (!nombre) return;
    const baseSlug = slugify(nombre);
    const id = generateUniqueId(
      baseSlug,
      new Set(categorias.map((c) => c.id))
    );
    const orden =
      categorias.length > 0
        ? Math.max(...categorias.map((c) => c.orden || 0)) + 1
        : 1;
    try {
      await setDoc(doc(db, "catalogos", CATEG_COLL, "items", id), {
        nombre,
        orden,
        activo: true,
      });
      setNewCategoriaName("");
    } catch (e) {
      showAlert("Error al agregar: " + e.message, "error");
    }
  };

  const handleAddSubcategoria = async () => {
    const nombre = newSubcategoriaName.trim();
    if (!nombre || !selectedCategoriaId) return;
    const baseSlug = `${selectedCategoriaId}-${slugify(nombre)}`;
    const id = generateUniqueId(
      baseSlug,
      new Set(subcategorias.map((s) => s.id))
    );
    const sameParent = subcategorias.filter(
      (s) => s.parentId === selectedCategoriaId
    );
    const orden =
      sameParent.length > 0
        ? Math.max(...sameParent.map((s) => s.orden || 0)) + 1
        : 1;
    try {
      await setDoc(doc(db, "catalogos", SUBCATEG_COLL, "items", id), {
        nombre,
        parentId: selectedCategoriaId,
        orden,
        activo: true,
      });
      setNewSubcategoriaName("");
    } catch (e) {
      showAlert("Error al agregar: " + e.message, "error");
    }
  };

  const handleSaveName = async (collName, id) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    try {
      await updateDoc(doc(db, "catalogos", collName, "items", id), {
        nombre: trimmed,
      });
      setEditingId(null);
      setEditName("");
    } catch (e) {
      showAlert("Error al guardar: " + e.message, "error");
    }
  };

  const handleToggleActivo = async (collName, item) => {
    try {
      await updateDoc(doc(db, "catalogos", collName, "items", item.id), {
        activo: !isActivo(item),
      });
    } catch (e) {
      showAlert("Error al actualizar: " + e.message, "error");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;
    try {
      await deleteDoc(
        doc(
          db,
          "catalogos",
          deleteCandidate.collName,
          "items",
          deleteCandidate.id
        )
      );
      setDeleteCandidate(null);
    } catch (e) {
      showAlert("Error al eliminar: " + e.message, "error");
    }
  };

  const handleDragEnd = async (result, type) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    const list =
      type === "categoria" ? [...categorias] : [...visibleSubcategorias];
    const [moved] = list.splice(src, 1);
    list.splice(dst, 0, moved);
    const collName = type === "categoria" ? CATEG_COLL : SUBCATEG_COLL;
    const batch = writeBatch(db);
    list.forEach((item, i) => {
      batch.update(doc(db, "catalogos", collName, "items", item.id), {
        orden: i + 1,
      });
    });
    try {
      await batch.commit();
    } catch (e) {
      showAlert("Error al reordenar: " + e.message, "error");
    }
  };

  const renderRow = (item, idx, collName, keyPrefix) => (
    <Draggable key={item.id} draggableId={`${keyPrefix}-${item.id}`} index={idx}>
      {(prov) => (
        <div
          ref={prov.innerRef}
          {...prov.draggableProps}
          className={`flex items-center gap-2 rounded-lg border border-zinc-200 px-2 py-1.5 ${
            isActivo(item) ? "bg-white" : "bg-zinc-50 opacity-60"
          }`}
        >
          <button
            type="button"
            {...prov.dragHandleProps}
            className="cursor-grab text-zinc-400 hover:text-zinc-600"
            title="Arrastrar para reordenar"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
          {editingId === `${keyPrefix}-${item.id}` ? (
            <>
              <input
                autoFocus
                type="text"
                className="flex-1 rounded border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveName(collName, item.id);
                  if (e.key === "Escape") setEditingId(null);
                }}
              />
              <button
                onClick={() => handleSaveName(collName, item.id)}
                className="text-emerald-600 hover:text-emerald-700"
                title="Guardar"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setEditingId(null)}
                className="text-zinc-400 hover:text-zinc-600"
                title="Cancelar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 text-sm">{item.nombre}</span>
              <button
                onClick={() => {
                  setEditingId(`${keyPrefix}-${item.id}`);
                  setEditName(item.nombre || "");
                }}
                className="text-zinc-400 hover:text-zinc-600"
                title="Editar nombre"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleToggleActivo(collName, item)}
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  isActivo(item)
                    ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
                }`}
              >
                {isActivo(item) ? "Activo" : "Inactivo"}
              </button>
              <button
                onClick={() =>
                  setDeleteCandidate({
                    collName,
                    id: item.id,
                    nombre: item.nombre,
                    type: collName === CATEG_COLL ? "categoria" : "subcategoria",
                  })
                }
                className="text-zinc-400 hover:text-red-500"
                title="Eliminar"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      )}
    </Draggable>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <Heading level={2} className="mb-2">
        Categorías de Proveedores
      </Heading>
      <p className="text-sm text-zinc-500 mb-6">
        Lista global que verán todos los venues al crear proveedores. Desactivar
        oculta la opción de nuevos selectores sin afectar proveedores existentes.
      </p>

      {/* Categorías */}
      <div className="mb-8 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
        <Heading level={3} className="mb-4">
          Categorías principales
        </Heading>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Nueva categoría"
            className="flex-1 rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            value={newCategoriaName}
            onChange={(e) => setNewCategoriaName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddCategoria();
            }}
          />
          <button
            onClick={handleAddCategoria}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
            style={{ backgroundColor: "#1b4d2e" }}
          >
            <PlusIcon className="h-4 w-4" />
            Agregar
          </button>
        </div>

        {categorias.length === 0 ? (
          <div className="text-sm text-zinc-400 italic py-4">
            Sin categorías.
          </div>
        ) : (
          <DragDropContext onDragEnd={(r) => handleDragEnd(r, "categoria")}>
            <Droppable droppableId="categorias">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-1.5"
                >
                  {categorias.map((cat, idx) =>
                    renderRow(cat, idx, CATEG_COLL, "cat")
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Subcategorías */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <Heading level={3}>Subcategorías</Heading>
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500">Categoría:</label>
            <select
              value={selectedCategoriaId || ""}
              onChange={(e) => setSelectedCategoriaId(e.target.value)}
              className="rounded-lg border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
              disabled={categorias.length === 0}
            >
              {categorias.length === 0 && <option value="">Sin categorías</option>}
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedCategoriaId && (
          <>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Nueva subcategoría"
                className="flex-1 rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                value={newSubcategoriaName}
                onChange={(e) => setNewSubcategoriaName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddSubcategoria();
                }}
              />
              <button
                onClick={handleAddSubcategoria}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
                style={{ backgroundColor: "#1b4d2e" }}
              >
                <PlusIcon className="h-4 w-4" />
                Agregar
              </button>
            </div>

            {visibleSubcategorias.length === 0 ? (
              <div className="text-sm text-zinc-400 italic py-4">
                Sin subcategorías para esta categoría.
              </div>
            ) : (
              <DragDropContext
                onDragEnd={(r) => handleDragEnd(r, "subcategoria")}
              >
                <Droppable droppableId="subcategorias">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-1.5"
                    >
                      {visibleSubcategorias.map((sub, idx) =>
                        renderRow(sub, idx, SUBCATEG_COLL, "sub")
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </>
        )}
      </div>

      <Alert
        open={!!deleteCandidate}
        onClose={() => setDeleteCandidate(null)}
      >
        <div className="font-semibold mb-2">
          ¿Eliminar "{deleteCandidate?.nombre}"?
        </div>
        <div className="mb-4 text-sm text-zinc-600">
          {deleteCandidate?.type === "categoria"
            ? "Los proveedores existentes que usan esta categoría conservarán su referencia, pero ya no aparecerá en los selectores. Sus subcategorías quedarán huérfanas."
            : "Los proveedores existentes con esta subcategoría conservarán su referencia, pero ya no aparecerá en los selectores."}
          {" Para ocultarla sin perder la referencia, usa el botón Activo."}
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleConfirmDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

export default ProveedoresCategoriasManager;
